import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import * as signalR from "@microsoft/signalr";
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  catchError,
  from,
  map,
  switchMap,
  tap,
} from "rxjs";
import { environment } from "../../environments/environment";
import { PaymentResult } from "../enums/payment-results.enum";
import { RoutingPaths } from "../enums/routing-paths.enum";
import {
  CreateTransitionBody,
  CreateTransitionResponse,
} from "../interfaces/payment.interface";
import { Response } from "../interfaces/response.interface";
import { LoaderService } from "./loader.service";

@Injectable({
  providedIn: "root",
})
export class PaymentService {
  private readonly http = inject(HttpClient);
  private readonly loaderService = inject(LoaderService);
  private readonly router = inject(Router);

  readonly paymentStatusListenerName = "paymentStatusChanged";
  private readonly apiUrl = environment.baseApiUrl;
  private signalRConnectionHub?: signalR.HubConnection;
  private onPaymentStatusChanged = new BehaviorSubject<
    PaymentResult | undefined
  >(undefined);

  openTransaction(
    hasCode: string,
    email: string,
    returnLinkSuffix?: string,
  ): Observable<CreateTransitionResponse> {
    const returnLink =
      (environment.production ? location.origin : "http://localhost") +
      (returnLinkSuffix ??
        `${environment.hostPrefix}/${RoutingPaths.scb}/${RoutingPaths.payment}`);

    return this.createTransaction(hasCode, {
      emailAddress: email,
      returnLink: returnLink,
    }).pipe(
      catchError(() => {
        this.loaderService.hide();
        this.router.navigate([RoutingPaths.scb, RoutingPaths.invalid]);
        return EMPTY;
      }),
      map((transaction) => transaction.result),
      tap((result) => (window.location.href = result.transactionLink)),
    );
  }

  getTransactionStatus(transactionId: string): Observable<PaymentResult> {
    return this.http
      .get<
        Response<PaymentResult>
      >(`${this.apiUrl}/public/policy-payment/transaction/${transactionId}`)
      .pipe(map((response) => response.result));
  }

  getTransactionStatusSignalR(
    transactionId: string,
  ): Observable<PaymentResult | undefined> {
    this.signalRConnectionHub = new signalR.HubConnectionBuilder()
      .withUrl(
        `${
          this.apiUrl
        }/paymentshub?transactionUuid=${transactionId.toLowerCase()}`,
        { withCredentials: false },
      )
      .build();

    return from(this.signalRConnectionHub.start()).pipe(
      tap(() => this.transactionStatusListener()),
      switchMap(() => this.onPaymentStatusChanged),
    );
  }

  private createTransaction(
    hashCode: string,
    createTransactionBody: CreateTransitionBody,
  ): Observable<Response<CreateTransitionResponse>> {
    return this.http.post<Response<CreateTransitionResponse>>(
      `${this.apiUrl}/public/policy-payment/transaction/policy/${hashCode}`,
      createTransactionBody,
    );
  }

  private transactionStatusListener() {
    this.signalRConnectionHub?.on("paymentStatusChanged", (status) => {
      this.onPaymentStatusChanged.next(status);
      if (status !== PaymentResult.Pending) this.signalRConnectionHub?.stop();
    });
  }
}
